import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import type { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseIcon } from './icons';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** `alertdialog` for a question that must be answered: a click outside does not close it. */
  role?: 'dialog' | 'alertdialog';
  /** Where focus starts; a form starts on its first field. Without it, the first control. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** A form with columns: wider than the default. */
  wide?: boolean;
  /** Under its title, held with it while the rest scrolls: a form's parts, as tabs. */
  bar?: ReactNode;
}

/** A modal panel: focus kept inside, Escape closes, the page behind does not scroll. */
export function Dialog({
  open,
  onClose,
  title,
  children,
  role = 'dialog',
  initialFocus,
  wide = false,
  bar = null,
}: DialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const { refs, context } = useFloating({ open, onOpenChange: onClose });
  const { getFloatingProps } = useInteractions([
    useDismiss(context, { outsidePress: role === 'dialog', outsidePressEvent: 'mousedown' }),
    useRole(context, { role }),
  ]);
  if (!open) {
    return null;
  }
  return (
    <FloatingPortal>
      <FloatingOverlay className="dialog-backdrop" lockScroll>
        <FloatingFocusManager context={context} initialFocus={initialFocus ?? 0}>
          <div
            ref={(node) => {
              refs.setFloating(node);
            }}
            className={wide ? 'dialog dialog-wide' : 'dialog'}
            aria-labelledby={titleId}
            {...getFloatingProps()}
          >
            <div className="dialog-head">
              <div className="dialog-title">
                <h2 id={titleId}>{title}</h2>
                <button
                  type="button"
                  className="icon-button icon-button-small"
                  aria-label={t('dialog.close')}
                  onClick={onClose}
                >
                  <CloseIcon />
                </button>
              </div>
              {bar}
            </div>
            {children}
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/** A question with two answers, in place of `window.confirm` (docs/02 §6). */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onCancel} title={title} role="alertdialog">
      <p className="dialog-body">{body}</p>
      <div className="dialog-actions">
        <button type="button" className="button" onClick={onCancel}>
          {t('dialog.cancel')}
        </button>
        <button type="button" className="button button-primary" onClick={onConfirm}>
          {confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}
